/**
 * analytics — Lightweight analytics service.
 * Logs to console in DEV; queues events for future vendor integration.
 */

export type AnalyticsEvent =
  | 'workout_saved'
  | 'duel_created'
  | 'duel_completed'
  | 'pr_set'
  | 'baseline_completed'
  | 'signup_completed'
  | 'subscription_started'
  | 'theme_changed'
  | (string & {});

interface QueuedEvent {
  name: string;
  properties?: Record<string, unknown>;
  timestamp: number;
}

const eventQueue: QueuedEvent[] = [];
let _userId: string | null = null;
let _userTraits: Record<string, unknown> = {};

export function initAnalytics() {
  if (__DEV__) console.log('[Analytics] Analytics initialized');
}

export function trackEvent(name: AnalyticsEvent, properties?: Record<string, unknown>) {
  const event: QueuedEvent = { name, properties, timestamp: Date.now() };

  if (__DEV__) {
    console.log('[Analytics] trackEvent:', name, properties ?? '');
  }

  eventQueue.push(event);

  // Cap queue size to prevent memory leaks
  if (eventQueue.length > 500) {
    eventQueue.splice(0, eventQueue.length - 500);
  }

  // Flush to Supabase (fire-and-forget, non-blocking)
  if (!__DEV__ && _userId) {
    try {
      const { supabase } = require('@/lib/supabase');
      supabase.from('analytics_events').insert({
        user_id: _userId,
        event_name: name,
        metadata: properties ?? {},
        created_at: new Date(event.timestamp).toISOString(),
      }).then(() => {}).catch(() => {});
    } catch { /* analytics should never crash the app */ }
  }
}

export function trackScreen(screenName: string) {
  trackEvent('screen_view', { screen: screenName });
}

export function identifyUser(userId: string, traits?: Record<string, unknown>) {
  _userId = userId;
  _userTraits = traits ?? {};
  if (__DEV__) {
    console.log('[Analytics] identifyUser:', userId, traits ?? '');
  }
}

/** Expose queue for future vendor flush */
export function getEventQueue(): readonly QueuedEvent[] {
  return eventQueue;
}
